/* Writers' Salon sign-in modal — the "exclusive" reskin of the old
 * EmailLoginModal that used to live inside `QuillAndVineLanding.jsx`.
 *
 * Behaviour is unchanged: the same POST /api/auth/password-login and
 * POST /api/auth/password-reset/request calls are made, and the same
 * three modes (`signin` / `forgot` / `forgot-sent`) exist. What
 * changes is the presentation — parchment card, wax-seal emblem,
 * gold rule dividers, invitation-only framing, keycode-style label.
 *
 * All original test-ids are preserved so existing tours & tests keep
 * working: cb-email-modal, cb-email-modal-email, cb-email-modal-password,
 * cb-email-modal-forgot, cb-email-modal-submit, cb-forgot-form,
 * cb-forgot-email, cb-forgot-submit, cb-forgot-sent, cb-forgot-back.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, X, Loader2, KeyRound, Feather, Sparkles, BookOpen, ScrollText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { P, FONTS, GRAIN_URL } from './palette';
import { useAuth } from '@/lib/auth_context';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

/* Reusable wax-seal graphic. Pure CSS — a circular oxblood disc with
 * a raised ring, embossed monogram, and dropped drip beneath. */
function WaxSeal() {
  return (
    <div className="pointer-events-none relative mx-auto h-16 w-16" aria-hidden>
      <div
        className="absolute inset-0 rounded-full"
        style={{
          background: `radial-gradient(circle at 35% 30%, ${P.oxbloodHi} 0%, ${P.oxblood} 45%, #3A1010 100%)`,
          boxShadow:  `0 6px 14px -6px rgba(0,0,0,0.6), inset 0 -3px 6px rgba(0,0,0,0.4), inset 0 2px 4px rgba(255,255,255,0.15)`,
        }}
      />
      <div
        className="absolute inset-1.5 rounded-full"
        style={{ border: `1px dashed ${P.gold}77` }}
      />
      <span
        className="absolute inset-0 flex items-center justify-center text-[22px]"
        style={{
          color:      P.gold,
          fontFamily: FONTS.display,
          fontWeight: 700,
          textShadow: `0 1px 0 rgba(0,0,0,0.6)`,
        }}
      >
        ℂ𝔅
      </span>
      {/* Small drip */}
      <span
        className="absolute -bottom-1 left-1/2 h-3 w-2 -translate-x-1/2 rounded-b-full"
        style={{
          background: `linear-gradient(180deg, ${P.oxblood}, #4A3A32)`,
          boxShadow: `0 2px 4px rgba(0,0,0,0.3)`,
        }}
      />
    </div>
  );
}

/* Section that wraps every mode with the same parchment aesthetic. */
function SalonShell({ children, testid, onClose, onBack, backTestId }) {
  return (
    <div
      className="relative w-[calc(100vw-1.5rem)] max-w-md overflow-hidden rounded-lg border shadow-2xl"
      data-testid={testid}
      style={{
        backgroundColor: P.paperDeep,
        borderColor:     P.gold,
        boxShadow:       `0 40px 80px -30px ${P.leather}f0, 0 0 0 1px ${P.gold}66`,
      }}
    >
      <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: GRAIN_URL }} />

      {/* Header — brand + close */}
      <div
        className="relative flex items-center justify-between border-b px-5 py-3"
        style={{ borderColor: `${P.gold}55`, backgroundColor: `${P.paper}f0` }}
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onBack}
            aria-label="Go back"
            title="Back"
            className="inline-flex h-8 w-8 items-center justify-center rounded-sm border"
            style={{ borderColor: `${P.gold}88`, color: P.oxblood, backgroundColor: P.paperDeep }}
            data-testid={backTestId}
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <Feather className="h-4 w-4" style={{ color: P.oxblood }} />
          <span className="text-[11px] uppercase tracking-[0.32em]" style={{ color: P.oxblood, fontFamily: FONTS.type }}>
            The Writers&rsquo; Salon
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid={`${testid}-close`}
          className="rounded-sm p-1 hover:bg-black/5"
          style={{ color: P.inkSoft }}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="relative px-4 py-5 sm:px-6 sm:py-6" style={{ backgroundColor: P.paper }}>
        {children}
      </div>
    </div>
  );
}

/* Gold rule with a small centered ornament. */
function OrnamentRule() {
  return (
    <div className="relative my-4 flex items-center gap-3" aria-hidden>
      <span className="h-px flex-1" style={{ backgroundColor: `${P.gold}88` }} />
      <span
        className="text-[10px]"
        style={{ color: P.gold, fontFamily: FONTS.display }}
      >
        ✦
      </span>
      <span className="h-px flex-1" style={{ backgroundColor: `${P.gold}88` }} />
    </div>
  );
}

export default function WritersSalonModal({ open, onClose, initialMode = 'signin' }) {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { refresh } = useAuth();
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ email: '', recoveryEmail: '', password: '', name: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  /* iter55f58 — ULA acceptance state for free signup.
   * User cannot submit until the checkbox is ticked; the ULA text is
   * expandable so it doesn't dominate the parchment form on load. */
  const [ulaAccepted, setUlaAccepted] = useState(false);
  const [ulaDialogOpen, setUlaDialogOpen] = useState(false);
  // 2026-10 — Free is the sole public signup path now; "trial" stays
  // wired for a future reactivation campaign (see routes_auth.py) but
  // is no longer offered in this modal's copy.
  const signupTier = 'free';
  React.useEffect(() => { if (open) setMode(initialMode); }, [open, initialMode]);
  if (!open) return null;

  const close = () => {
    setMode(initialMode);
    setForm({ email: '', recoveryEmail: '', password: '', name: '', confirm: '' });
    onClose();
  };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await http.post('/auth/password-login', { email: form.email, password: form.password })
                          .then((r2) => r2.data);
      toast.success(`Welcome back, ${r.user.name || r.user.email}.`);
      /* iter-72f — refresh AuthProvider state before navigating so the
       * /app route doesn't render the "sign in to continue" gate. */
      await refresh();
      navigate('/app');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Sign-in failed.');
    } finally { setBusy(false); }
  };
  const sendReset = async (e) => {
    e.preventDefault();
    if (!form.email) { toast.error('Enter your email first.'); return; }
    setBusy(true);
    try {
      await http.post('/auth/password-reset/request', { email: form.email });
      setMode('forgot-sent');
    } catch {
      setMode('forgot-sent');
    } finally { setBusy(false); }
  };
  /* iter-72f — Free signup. Backend mints the session cookie so on
   * success we drop the visitor straight into /app. */
  const signup = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    if (form.password !== form.confirm) { toast.error('Passwords do not match.'); return; }
    /* iter55f58 — Enforce ULA acceptance client-side. Backend also
     * requires `ula_accepted: true` on the request body so a scripted
     * bypass fails at the API layer too. */
    if (!ulaAccepted) { toast.error('Please accept the User Agreement to continue.'); return; }
    setBusy(true);
    try {
      // iter55z-audit round 9 — Attach sticky UTM attribution so the
      // Admin → Financials dashboard can slice by acquisition source.
      // First-touch wins; falls back to referrer hostname or 'direct'.
      const { readStickyUtms } = await import('@/lib/utmTracking');
      const utms = readStickyUtms();
      await http.post('/auth/trial-signup', {
        email: form.email, recovery_email: form.recoveryEmail,
        password: form.password, name: form.name || null,
        language: (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0].toLowerCase(),
        ula_accepted: true,
        signup_tier: signupTier,
        utm_source:   utms.utm_source,
        utm_medium:   utms.utm_medium,
        utm_campaign: utms.utm_campaign,
        click_id:     utms.click_id,
        ref_code:     utms.ref_code,
      });
      toast.success("You're in — free to get started. Welcome to The Loom.");
      /* iter-72f — refresh AuthProvider before navigating; otherwise
       * /app renders the sign-in gate until the user manually reloads. */
      await refresh();
      navigate('/app');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create your account.');
    } finally { setBusy(false); }
  };

  /* 2026-02 · Reader Lane — Readers who wander into the writer-only
   * salon can create a Book Store account without leaving the door.
   * Posts to /bookstore/auth/register (separate from The Loom auth;
   * bookstore has its own session cookie). On success we bounce to
   * /bookstore so they can start browsing/buying immediately. */
  const readerSignup = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    if (form.password !== form.confirm) { toast.error('Passwords do not match.'); return; }
    setBusy(true);
    try {
      await http.post('/bookstore/auth/register', {
        email: form.email,
        password: form.password,
        name: form.name || null,
      });
      toast.success('Welcome to the Book Store.');
      navigate('/bookstore');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create your reader account.');
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8"
      role="dialog"
      aria-modal="true"
      data-testid="cb-email-modal"
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
      style={{ backgroundColor: 'rgba(20, 12, 8, 0.75)', backdropFilter: 'blur(4px)' }}
    >
      {mode === 'signin' && (
        <SalonShell onClose={close} onBack={close} backTestId="cb-salon-signin-back" testid="cb-salon-signin">
          <div className="text-center">
            <WaxSeal />
            <div
              className="mt-3 text-[10px] uppercase tracking-[0.36em]"
              style={{ color: P.oxblood, fontFamily: FONTS.type }}
            >
              For working novelists · free to try
            </div>
            <h2
              className="mt-1 text-2xl leading-tight"
              style={{ fontFamily: FONTS.display, fontWeight: 600, color: P.ink }}
            >
              Enter the Writers&rsquo; Salon
            </h2>
            <p
              className="mx-auto mt-2 max-w-xs text-[12.5px] leading-relaxed"
              style={{ color: P.inkSoft, fontFamily: FONTS.body }}
            >
              A private studio for working novelists. Enter your password below to open the door.
            </p>
            {/* 2026-02 · Audience clarifier — the salon is a
             *  writer-only door. Readers who wander in from the
             *  Bookstore get a couple of clear signposts back out
             *  AND an in-place reader-signup option so they never
             *  land in the writer-only dead-letter box. */}
            <p
              className="mx-auto mt-3 max-w-xs text-[10.5px] leading-relaxed"
              style={{ color: P.inkSoft, fontFamily: FONTS.body }}
              data-testid="cb-salon-audience-note"
            >
              For novelists and copywriters using The Loom. Readers, head to
              the{' '}
              <a href="/bookstore" className="font-semibold hover:underline"
                  style={{ color: P.oxblood }}
                  data-testid="cb-salon-audience-bookstore-link">
                Book Store
              </a>{' '}or an{' '}
              <a href="/authors" className="font-semibold hover:underline"
                  style={{ color: P.oxblood }}
                  data-testid="cb-salon-audience-authors-link">
                author&rsquo;s page
              </a>{' '}to say hello.
            </p>
            {/* 2026-02 · Reader Lane — one-click switch into a
             *  reader-signup flow. Posts to /bookstore/auth/register
             *  and bounces to the Book Store on success. No more
             *  readers stuck bouncing between the salon door and a
             *  mailto: link that leads nowhere. */}
            <button
              type="button"
              onClick={() => setMode('reader-signup')}
              data-testid="cb-salon-open-reader-signup"
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10.5px] font-semibold uppercase tracking-[0.14em] transition hover:brightness-110"
              style={{
                borderColor: `${P.oxblood}66`,
                backgroundColor: `${P.paperDeep}`,
                color: P.oxblood,
                fontFamily: FONTS.display,
              }}
            >
              <BookOpen className="h-3 w-3" />
              I&rsquo;m a reader — sign me up
            </button>
          </div>

          <OrnamentRule />

          <form onSubmit={submit} className="space-y-3">
            <label className="block">
              <span
                className="mb-1 block text-[10.5px] uppercase tracking-[0.24em]"
                style={{ color: P.inkSoft, fontFamily: FONTS.type }}
              >
                Correspondence address
              </span>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="cb-email-modal-email"
                placeholder="you@yourdomain.com"
                className="w-full rounded-md border px-3 py-2.5 text-[13.5px] outline-none focus:ring-2"
                style={{
                  backgroundColor: P.paperDeep,
                  borderColor:     `${P.gold}88`,
                  color:           P.ink,
                  fontFamily:      FONTS.body,
                }}
              />
            </label>
            <label className="block">
              <div className="mb-1 flex items-center justify-between">
                <span
                  className="text-[10.5px] uppercase tracking-[0.24em]"
                  style={{ color: P.inkSoft, fontFamily: FONTS.type }}
                >
                  Password
                </span>
                <button
                  type="button"
                  onClick={() => setMode('forgot')}
                  data-testid="cb-email-modal-forgot"
                  className="text-[10.5px] font-semibold hover:underline"
                  style={{ color: P.oxblood, fontFamily: FONTS.display }}
                >
                  Lost your password?
                </button>
              </div>
              <input
                type="password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="cb-email-modal-password"
                placeholder="••••••••"
                className="w-full rounded-md border px-3 py-2.5 text-[13.5px] outline-none focus:ring-2"
                style={{
                  backgroundColor: P.paperDeep,
                  borderColor:     `${P.gold}88`,
                  color:           P.ink,
                  fontFamily:      FONTS.body,
                }}
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              data-testid="cb-email-modal-submit"
              className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-[13px] font-semibold shadow-md transition hover:brightness-110 disabled:opacity-60"
              style={{ backgroundColor: P.oxblood, color: P.gold, fontFamily: FONTS.display }}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
              Open the salon door
            </button>
          </form>

          <OrnamentRule />

          <p
            className="text-center text-[10.5px]"
            style={{ color: P.inkSoft, fontFamily: FONTS.body }}
          >
            Not a member yet?{' '}
            <button
              type="button"
              onClick={() => setMode('signup')}
              data-testid="cb-salon-open-trial"
              className="font-semibold hover:underline"
              style={{ color: P.oxblood, fontFamily: FONTS.display, background: 'none', border: 0, padding: 0 }}
            >
              Start free — no card needed
            </button>
          </p>
          {/* 2026-02 · Team-access ask — replaces the old "Request an
           *  invitation" mailto that was ambiguous about who it was
           *  for. Free signup IS the self-serve invitation for
           *  individual writers; this row is now a genuine studio /
           *  team enquiry so `info@` gets qualified leads instead of
           *  "what is this". */}
          <p
            className="mt-2 text-center text-[10.5px]"
            style={{ color: P.inkSoft, fontFamily: FONTS.body }}
          >
            Team of writers or studio?{' '}
            <a
              href="mailto:info@crownnrose.com?subject=Team%20access%20enquiry&body=Hello%20Crown%20%26%20Rose%2C%0A%0AWe're%20a%20team%20of%20____%20writers%20and%20we'd%20like%20to%20explore%20studio%20access.%0A%0AThank%20you%2C"
              data-testid="cb-salon-team-enquiry"
              className="font-semibold hover:underline"
              style={{ color: P.oxblood, fontFamily: FONTS.display }}
            >
              Talk to us →
            </a>
          </p>
          {/* Staff entrance — unobtrusive, for internal team access */}
          <p
            className="mt-3 text-center text-[9.5px]"
            style={{ color: `${P.inkSoft}88`, fontFamily: FONTS.body }}
          >
            <button
              type="button"
              data-testid="cb-salon-staff-entrance"
              onClick={() => { close(); navigate('/login'); }}
              className="hover:underline transition-opacity opacity-60 hover:opacity-100"
              style={{ color: P.inkSoft, background: 'none', border: 0, padding: 0, cursor: 'pointer', fontFamily: FONTS.body }}
            >
              Staff Entrance
            </button>
          </p>
        </SalonShell>
      )}

      {mode === 'signup' && (
        <SalonShell onClose={close} onBack={() => setMode('signin')} backTestId="cb-salon-signup-hierarchy-back" testid="cb-salon-signup">
          <div className="text-center">
            <WaxSeal />
            <div
              className="mt-3 text-[10px] uppercase tracking-[0.32em]"
              style={{ color: P.oxblood, fontFamily: FONTS.type }}
            >
              Free to try · no card required
            </div>
            <h2
              className="mt-1 text-xl sm:text-2xl leading-tight"
              style={{ fontFamily: FONTS.display, fontWeight: 600, color: P.ink }}
            >
              Write your story, free to start
            </h2>
            <p
              className="mx-auto mt-2 w-full max-w-xs text-[11.5px] sm:text-[12.5px]"
              style={{ color: P.inkSoft, fontFamily: FONTS.body }}
            >
              Draft, edit, and refine your novel with the full Starter toolkit — free to get started, no card, no commitment.
            </p>
          </div>

          <OrnamentRule />

          <form onSubmit={signup} className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-[10.5px] uppercase tracking-[0.24em]"
                    style={{ color: P.inkSoft, fontFamily: FONTS.type }}>Pen name</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="cb-salon-signup-name"
                placeholder="e.g. A. N. Author"
                maxLength={80}
                className="w-full rounded-md border px-3 py-2.5 text-[13.5px] outline-none focus:ring-2"
                style={{ backgroundColor: P.paperDeep, borderColor: `${P.gold}88`, color: P.ink, fontFamily: FONTS.body }}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10.5px] uppercase tracking-[0.24em]"
                    style={{ color: P.inkSoft, fontFamily: FONTS.type }}>Correspondence address</span>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="cb-salon-signup-email"
                placeholder="you@yourdomain.com"
                className="w-full rounded-md border px-3 py-2.5 text-[13.5px] outline-none focus:ring-2"
                style={{ backgroundColor: P.paperDeep, borderColor: `${P.gold}88`, color: P.ink, fontFamily: FONTS.body }}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10.5px] uppercase tracking-[0.24em]"
                    style={{ color: P.inkSoft, fontFamily: FONTS.type }}>Recovery email you own</span>
              <input
                type="email"
                required
                value={form.recoveryEmail}
                onChange={(e) => setForm({ ...form, recoveryEmail: e.target.value })}
                data-testid="cb-salon-signup-recovery-email"
                placeholder="your.personal@email.com"
                className="w-full rounded-md border px-3 py-2.5 text-[13.5px] outline-none focus:ring-2"
                style={{ backgroundColor: P.paperDeep, borderColor: `${P.gold}88`, color: P.ink, fontFamily: FONTS.body }}
              />
              <span className="mt-1 block text-[9.5px] leading-relaxed" style={{ color: P.inkSoft }}>Used for account recovery and, after verification, as an alternate login.</span>
            </label>
            <label className="block">
              <span className="mb-1 block text-[10.5px] uppercase tracking-[0.24em]"
                    style={{ color: P.inkSoft, fontFamily: FONTS.type }}>Choose a password · min 8 characters</span>
              <input
                type="password"
                required
                minLength={8}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="cb-salon-signup-password"
                placeholder="••••••••"
                className="w-full rounded-md border px-3 py-2.5 text-[13.5px] outline-none focus:ring-2"
                style={{ backgroundColor: P.paperDeep, borderColor: `${P.gold}88`, color: P.ink, fontFamily: FONTS.body }}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10.5px] uppercase tracking-[0.24em]"
                    style={{ color: P.inkSoft, fontFamily: FONTS.type }}>Confirm password</span>
              <input
                type="password"
                required
                minLength={8}
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                data-testid="cb-salon-signup-confirm"
                placeholder="••••••••"
                className="w-full rounded-md border px-3 py-2.5 text-[13.5px] outline-none focus:ring-2"
                style={{ backgroundColor: P.paperDeep, borderColor: `${P.gold}88`, color: P.ink, fontFamily: FONTS.body }}
              />
            </label>
            {/* 2026-10 — Free-signup ULA. Compact acceptance row
             * with an inline expander for the full text. Users cannot
             * submit until the checkbox is ticked. */}
            <div className="rounded-md border p-2.5" style={{ borderColor: `${P.gold}88`, backgroundColor: `${P.paperDeep}` }}>
              <label className="flex items-start gap-2 text-[11.5px] cursor-pointer" style={{ color: P.ink, fontFamily: FONTS.body }}>
                <input
                  type="checkbox"
                  checked={ulaAccepted}
                  onChange={(e) => setUlaAccepted(e.target.checked)}
                  data-testid="cb-salon-signup-ula"
                  className="mt-0.5"
                />
                <span>
                  I accept the{' '}
                  <button
                    type="button"
                    onClick={() => setUlaDialogOpen(true)}
                    data-testid="cb-salon-signup-ula-toggle"
                    className="font-semibold underline"
                    style={{ color: P.oxblood, background: 'none', border: 0, padding: 0 }}
                  >
                    User Agreement
                  </button>
                  .
                </span>
              </label>
            </div>
            <button
              type="submit"
              disabled={busy || !ulaAccepted || !form.recoveryEmail}
              data-testid="cb-salon-signup-submit"
              className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-[13px] font-semibold shadow-md transition hover:brightness-110 disabled:opacity-60"
              style={{ backgroundColor: P.oxblood, color: P.gold, fontFamily: FONTS.display }}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Begin writing — free to try
            </button>
          </form>

          <p className="mt-4 text-center text-[11px]">
            <button
              type="button"
              onClick={() => setMode('signin')}
              data-testid="cb-salon-signup-back"
              className="font-semibold hover:underline"
              style={{ color: P.oxblood, fontFamily: FONTS.display, background: 'none', border: 0, padding: 0 }}
            >
              ← Already have a password? Sign in
            </button>
          </p>
        </SalonShell>
      )}

      {mode === 'reader-signup' && (
        <SalonShell onClose={close} onBack={() => setMode('signin')} backTestId="cb-salon-reader-hierarchy-back" testid="cb-salon-reader-signup">
          <div className="text-center">
            <WaxSeal />
            <div
              className="mt-3 text-[10px] uppercase tracking-[0.32em]"
              style={{ color: P.oxblood, fontFamily: FONTS.type }}
            >
              Book Store · reader account
            </div>
            <h2
              className="mt-1 text-2xl leading-tight"
              style={{ fontFamily: FONTS.display, fontWeight: 600, color: P.ink }}
            >
              Join the Book Store
            </h2>
            <p
              className="mx-auto mt-2 max-w-xs text-[12.5px]"
              style={{ color: P.inkSoft, fontFamily: FONTS.body }}
            >
              A quick reader account — save favourite books, follow authors,
              and get a one-time 20% welcome discount on your first buy.
              Not a writer account — no manuscript tools.
            </p>
          </div>

          <OrnamentRule />

          <form onSubmit={readerSignup} className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-[10.5px] uppercase tracking-[0.24em]"
                    style={{ color: P.inkSoft, fontFamily: FONTS.type }}>Reader name</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="cb-salon-reader-name"
                placeholder="e.g. Casey Reader"
                maxLength={80}
                className="w-full rounded-md border px-3 py-2.5 text-[13.5px] outline-none focus:ring-2"
                style={{ backgroundColor: P.paperDeep, borderColor: `${P.gold}88`, color: P.ink, fontFamily: FONTS.body }}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10.5px] uppercase tracking-[0.24em]"
                    style={{ color: P.inkSoft, fontFamily: FONTS.type }}>Email</span>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="cb-salon-reader-email"
                placeholder="you@yourdomain.com"
                className="w-full rounded-md border px-3 py-2.5 text-[13.5px] outline-none focus:ring-2"
                style={{ backgroundColor: P.paperDeep, borderColor: `${P.gold}88`, color: P.ink, fontFamily: FONTS.body }}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10.5px] uppercase tracking-[0.24em]"
                    style={{ color: P.inkSoft, fontFamily: FONTS.type }}>Choose a password · min 8 characters</span>
              <input
                type="password"
                required
                minLength={8}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="cb-salon-reader-password"
                placeholder="••••••••"
                className="w-full rounded-md border px-3 py-2.5 text-[13.5px] outline-none focus:ring-2"
                style={{ backgroundColor: P.paperDeep, borderColor: `${P.gold}88`, color: P.ink, fontFamily: FONTS.body }}
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10.5px] uppercase tracking-[0.24em]"
                    style={{ color: P.inkSoft, fontFamily: FONTS.type }}>Confirm password</span>
              <input
                type="password"
                required
                minLength={8}
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                data-testid="cb-salon-reader-confirm"
                placeholder="••••••••"
                className="w-full rounded-md border px-3 py-2.5 text-[13.5px] outline-none focus:ring-2"
                style={{ backgroundColor: P.paperDeep, borderColor: `${P.gold}88`, color: P.ink, fontFamily: FONTS.body }}
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              data-testid="cb-salon-reader-submit"
              className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-[13px] font-semibold shadow-md transition hover:brightness-110 disabled:opacity-60"
              style={{ backgroundColor: P.oxblood, color: P.gold, fontFamily: FONTS.display }}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookOpen className="h-4 w-4" />}
              Open my reader account
            </button>
          </form>

          <p className="mt-4 text-center text-[11px]">
            <button
              type="button"
              onClick={() => setMode('signin')}
              data-testid="cb-salon-reader-back"
              className="font-semibold hover:underline"
              style={{ color: P.oxblood, fontFamily: FONTS.display, background: 'none', border: 0, padding: 0 }}
            >
              ← Actually, I&rsquo;m a writer — take me back
            </button>
          </p>
        </SalonShell>
      )}

      {mode === 'forgot' && (
        <SalonShell onClose={close} onBack={() => setMode('signin')} backTestId="cb-forgot-hierarchy-back" testid="cb-forgot-form">
          <div className="text-center">
            <div
              className="text-[10px] uppercase tracking-[0.32em]"
              style={{ color: P.oxblood, fontFamily: FONTS.type }}
            >
              Recovery
            </div>
            <h2
              className="mt-1 text-2xl leading-tight"
              style={{ fontFamily: FONTS.display, fontWeight: 600, color: P.ink }}
            >
              Mislaid your password?
            </h2>
            <p
              className="mx-auto mt-2 max-w-xs text-[12.5px]"
              style={{ color: P.inkSoft, fontFamily: FONTS.body }}
            >
              We&rsquo;ll dispatch a one-time reset link to your correspondence
              address. It stays sealed for sixty minutes.
            </p>
            {/* 2026-02 · Mirror the audience clarifier from the sign-in
             *  screen so a reader who lands here still gets a clear
             *  signpost back to the Bookstore. */}
            <p
              className="mx-auto mt-3 max-w-xs text-[10.5px] leading-relaxed"
              style={{ color: P.inkSoft, fontFamily: FONTS.body }}
              data-testid="cb-forgot-audience-note"
            >
              This recovery flow is for writer accounts on The Loom.
              Readers looking for a book, head to the{' '}
              <a href="/bookstore" className="font-semibold hover:underline"
                  style={{ color: P.oxblood }}
                  data-testid="cb-forgot-audience-bookstore-link">
                Book Store
              </a>.
            </p>
          </div>

          <OrnamentRule />

          <form onSubmit={sendReset} className="space-y-3">
            <label className="block">
              <span
                className="mb-1 block text-[10.5px] uppercase tracking-[0.24em]"
                style={{ color: P.inkSoft, fontFamily: FONTS.type }}
              >
                Correspondence address
              </span>
              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="cb-forgot-email"
                placeholder="you@yourdomain.com"
                className="w-full rounded-md border px-3 py-2.5 text-[13.5px] outline-none"
                style={{
                  backgroundColor: P.paperDeep,
                  borderColor:     `${P.gold}88`,
                  color:           P.ink,
                  fontFamily:      FONTS.body,
                }}
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              data-testid="cb-forgot-submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-md px-4 py-3 text-[13px] font-semibold shadow-md transition hover:brightness-110 disabled:opacity-60"
              style={{ backgroundColor: P.oxblood, color: P.gold, fontFamily: FONTS.display }}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Dispatch the reset link
            </button>
          </form>

          <p className="mt-4 text-center text-[11px]">
            <button
              type="button"
              onClick={() => setMode('signin')}
              className="font-semibold hover:underline"
              style={{ color: P.oxblood, fontFamily: FONTS.display }}
            >
              ← Back to the salon door
            </button>
          </p>
        </SalonShell>
      )}

      {mode === 'forgot-sent' && (
        <SalonShell onClose={close} onBack={() => setMode('forgot')} backTestId="cb-forgot-sent-hierarchy-back" testid="cb-forgot-sent">
          <div className="text-center">
            <WaxSeal />
            <div
              className="mt-3 text-[10px] uppercase tracking-[0.32em]"
              style={{ color: P.oxblood, fontFamily: FONTS.type }}
            >
              Dispatched
            </div>
            <h2
              className="mt-1 text-2xl leading-tight"
              style={{ fontFamily: FONTS.display, fontWeight: 600, color: P.ink }}
            >
              Check your correspondence
            </h2>
            <p
              className="mx-auto mt-3 max-w-xs text-[13px]"
              style={{ color: P.ink, fontFamily: FONTS.body }}
            >
              If an account exists for{' '}
              <b style={{ color: P.oxblood }}>{form.email}</b>, a sealed reset
              link is on its way. It stays valid for sixty minutes.
            </p>
          </div>

          <OrnamentRule />

          <button
            type="button"
            onClick={() => setMode('signin')}
            data-testid="cb-forgot-back"
            className="inline-flex w-full items-center justify-center gap-2 rounded-md border px-4 py-3 text-[13px] font-semibold"
            style={{
              borderColor: P.oxblood,
              color:       P.oxblood,
              backgroundColor: P.paperDeep,
              fontFamily:  FONTS.display,
            }}
          >
            Back to the salon door
          </button>
        </SalonShell>
      )}
      {/* ULA full-text Dialog — opened by "User Agreement" link in signup form */}
      <UlaDialogModal open={ulaDialogOpen} onClose={() => setUlaDialogOpen(false)} />
    </div>
  );
}

/* ── ULA Dialog ────────────────────────────────────────────────────────────
 * Fetches /api/eula/current on open and renders the full User Agreement
 * in a scrollable modal. The inline toggle/expander is removed; this is
 * the sole way to read the text before accepting.
 * ─────────────────────────────────────────────────────────────────────── */
function UlaDialogModal({ open, onClose }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/eula/current`)
      .then((r) => setText(r.data?.markdown || r.data?.text || r.data?.content || ''))
      .catch(() => setText('Unable to load the User Agreement. Please try again.'))
      .finally(() => setLoading(false));
  }, [open]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="max-h-[80vh] max-w-2xl overflow-hidden flex flex-col"
        data-testid="ula-dialog"
        style={{ borderColor: `${P.gold}88`, backgroundColor: P.paper }}
      >
        <DialogHeader className="shrink-0 border-b pb-3" style={{ borderColor: `${P.gold}55` }}>
          <DialogTitle
            className="flex items-center gap-2 text-[15px]"
            style={{ fontFamily: FONTS.display, color: P.ink }}
          >
            <ScrollText className="h-4 w-4" style={{ color: P.oxblood }} />
            User License Agreement
          </DialogTitle>
        </DialogHeader>
        <div
          className="flex-1 overflow-y-auto py-3 pr-1 text-[12.5px] leading-relaxed"
          data-testid="ula-dialog-text"
          style={{ color: P.ink, fontFamily: FONTS.body }}
        >
          {loading
            ? <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" style={{ color: P.oxblood }} /></div>
            : <pre className="whitespace-pre-wrap font-sans">{text}</pre>
          }
        </div>
        <div className="shrink-0 flex justify-end border-t pt-3" style={{ borderColor: `${P.gold}55` }}>
          <button
            type="button"
            onClick={onClose}
            data-testid="ula-dialog-close"
            className="rounded-md px-4 py-2 text-[12.5px] font-semibold transition hover:brightness-110"
            style={{ backgroundColor: P.oxblood, color: P.gold, fontFamily: FONTS.display }}
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
